import React, { useState, useEffect, useCallback, useMemo, useRef } from "react";
import { useParams, useNavigate } from "react-router-dom";
import ReactQuill from "react-quill";
import "react-quill/dist/quill.snow.css";
import {
  getFirestore,
  doc,
  getDoc,
  updateDoc,
  addDoc,
  collection,
  query,
  orderBy,
  limit,
  getDocs,
  deleteDoc,
} from "firebase/firestore";
import { getAuth } from "firebase/auth";
import { getStudentById } from "../services/studentService.js";
import "../styles/EditArticlePage.css";
import { useTranslation } from "react-i18next";
import AIAnalysisPanel from "../components/AIAnalysisPanel.js";
import InlineMarkupEditor from "../components/InlineMarkupEditor.js";

const EditArticlePage = () => {
  const { id } = useParams();
  const { t } = useTranslation();
  const navigate = useNavigate();

  // Core fields
  const [title, setTitle] = useState("");
  const [content, setContent] = useState("");
  const [language, setLanguage] = useState("English");
  const [articleId, setArticleId] = useState(null);
  const [articleImage, setArticleImage] = useState("");

  // Category (select + free entry)
  const buildDefaultCategories = useCallback(
    () => [
      t("articleEditPage.categories.tech", "Tech"),
      t("articleEditPage.categories.sports", "Sports"),
      t("articleEditPage.categories.TDAH", "TDAH"),
      t("articleEditPage.categories.adultTDAH", "Adult TDAH"),
      t("articleEditPage.categories.children", "Children"),
      t("articleEditPage.categories.flow", "Flow"),
      t("articleEditPage.categories.mentalHealth", "Mental Health"),
      t("articleEditPage.categories.health", "Health"),
      t("articleEditPage.categories.education", "Education"),
      t("articleEditPage.categories.enem", "ENEM"),
    ],
    [t]
  );
  const [categories, setCategories] = useState(buildDefaultCategories);
  const [category, setCategory] = useState("Tech");
  const [newCategory, setNewCategory] = useState("");

  // Keywords (array of strings)
  const [keywords, setKeywords] = useState([]);
  const [keywordInput, setKeywordInput] = useState("");

  // State & auth
  const [loading, setLoading] = useState(!!id);
  const [error, setError] = useState(null);
  const [success, setSuccess] = useState(false);
  const [inlineErrors, setInlineErrors] = useState([]);
  const [showMarkups, setShowMarkups] = useState(false);

  const quillRef = useRef(null);
  const auth = getAuth();
  const currentUser = auth.currentUser;
  const [isStudent, setIsStudent] = useState(false);

  // ---------- Utilities ----------
  const normalizeString = (s) => s.trim().replace(/\s+/g, " ");

  const addCategory = (raw) => {
    const c = normalizeString(raw);
    if (!c) return;
    const exists = categories.some((x) => x.toLowerCase() === c.toLowerCase());
    if (!exists) {
      setCategories((prev) => [...prev, c]);
    }
    setCategory(c);
  };

  const addKeyword = (raw) => {
    const k = normalizeString(raw);
    if (!k) return;
    const exists = keywords.some((x) => x.toLowerCase() === k.toLowerCase());
    if (!exists) setKeywords((prev) => [...prev, k]);
  };

  const removeKeyword = (k) => {
    setKeywords((prev) => prev.filter((x) => x !== k));
  };

  const handleKeywordKeyDown = (e) => {
    if (e.key === "Enter" || e.key === "," || e.key === "Tab") {
      e.preventDefault();
      if (keywordInput) {
        addKeyword(keywordInput);
        setKeywordInput("");
      }
    }
  };

  const handleKeywordPaste = (e) => {
    const text = e.clipboardData.getData("text");
    if (text && (text.includes(",") || text.includes("\n") || text.includes(";"))) {
      e.preventDefault();
      const parts = text.split(/[,;\n]/).map(normalizeString).filter(Boolean);
      const next = [...keywords];
      for (const p of parts) {
        if (!next.some((x) => x.toLowerCase() === p.toLowerCase())) {
          next.push(p);
        }
      }
      setKeywords(next);
    }
  };

  // ---------- Effects ----------
  // Keep default categories in sync with language, preserving custom additions.
  useEffect(() => {
    const defaults = buildDefaultCategories();
    setCategories((prev) => {
      const custom = prev.filter(
        (c) => !defaults.some((d) => d.toLowerCase() === c.toLowerCase())
      );
      // Keep order: defaults first, then custom already present
      return [...defaults, ...custom];
    });
    // If current category disappeared due to language switch, re-add it.
    setCategory((prev) => prev || defaults[0]);
  }, [buildDefaultCategories]);

  // Check user type (student) for authorization rules
  useEffect(() => {
    const checkUserType = async () => {
      if (!currentUser) return;
      try {
        const student = await getStudentById(currentUser.uid).catch(() => null);
        if (student) setIsStudent(true);
      } catch (err) {
        console.error("Error checking user type:", err);
      }
    };
    checkUserType();
  }, [currentUser]);

  // Fetch next incremental article id
  const fetchNextArticleId = useCallback(async () => {
    const articlesQuery = query(
      collection(getFirestore(), "news"),
      orderBy("id", "desc"),
      limit(1)
    );
    const articlesSnapshot = await getDocs(articlesQuery);
    if (articlesSnapshot.docs.length > 0) {
      const lastArticle = articlesSnapshot.docs[0].data();
      return (lastArticle.id || 0) + 1;
    }
    return 1;
  }, []);

  // Load existing article or prepare new one
  useEffect(() => {
    const load = async () => {
      if (id) {
        try {
          const ref = doc(getFirestore(), "news", id);
          const snap = await getDoc(ref);
          if (!snap.exists()) {
            setError(t("articleEditPage.error.articleNotFound", "Article not found."));
            setLoading(false);
            return;
          }
          const data = snap.data();

          // Authorization for students
          if (isStudent && data.authorId && data.authorId !== currentUser?.uid) {
            setError(t("articleEditPage.error.notAuthorized", "You are not authorized to edit this article."));
            setLoading(false);
            return;
          }

          setTitle(data.title || "");
          setContent(data.content || "");
          setLanguage(data.language || "English");
          setArticleId(data.id || null);
          const cat = data.category || "Tech";
          setCategory(cat);
          // Ensure category exists in the selectable list
          setCategories((prev) => {
            if (!prev.some((x) => x.toLowerCase() === (cat || "").toLowerCase())) {
              return [...prev, cat];
            }
            return prev;
          });

          setKeywords(Array.isArray(data.keywords) ? data.keywords : []);
          setLoading(false);
        } catch (err) {
          console.error(err);
          setError(t("articleEditPage.error.fetchError", "Error fetching the article."));
          setLoading(false);
        }
      } else {
        const nextId = await fetchNextArticleId();
        setArticleId(nextId);
      }
    };
    load();
  }, [id, isStudent, currentUser, t, fetchNextArticleId]);

  // ---------- Handlers ----------
  const handleContentChange = useCallback((htmlValue) => {
    setContent(htmlValue);
  }, []);

  const handleInlineErrorsUpdate = useCallback((errors) => {
    setInlineErrors(errors || []);
    if (errors && errors.length > 0) setShowMarkups(true);
  }, []);

  const handleImageUpload = (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    const reader = new FileReader();
    reader.onloadend = () => setArticleImage(reader.result);
    reader.readAsDataURL(file);
  };

  const quillModules = useMemo(
    () => ({
      toolbar: {
        container: [
          [{ header: "1" }, { header: "2" }, { font: [] }],
          [{ size: [] }],
          ["bold", "italic", "underline", "strike", "blockquote"],
          [{ list: "ordered" }, { list: "bullet" }],
          ["link", "image"],
          ["clean"],
        ],
      },
    }),
    []
  );

  const handleSubmit = async (e) => {
    e.preventDefault();

    if (!currentUser) {
      setError(t("articleEditPage.error.notLoggedIn", "You must be logged in."));
      return;
    }

    // finalize pending keyword input
    if (keywordInput) {
      addKeyword(keywordInput);
      setKeywordInput("");
    }

    // sanitize keywords (unique, trimmed)
    const finalKeywords = Array.from(
      new Map(
        keywords
          .map((k) => [k.toLowerCase(), normalizeString(k)])
          .filter(([, v]) => v)
      ).values()
    );

    try {
      if (id) {
        const ref = doc(getFirestore(), "news", id);
        const snap = await getDoc(ref);
        if (isStudent && snap.exists() && snap.data().authorId !== currentUser.uid) {
          setError(t("articleEditPage.error.notAuthorized", "You are not authorized to edit this article."));
          return;
        }
        await updateDoc(ref, {
          title,
          content,
          category,
          language,
          id: articleId,
          image: articleImage,
          keywords: finalKeywords,
          updatedAt: new Date().toISOString(),
        });
        setSuccess(true);
      } else {
        const newRef = await addDoc(collection(getFirestore(), "news"), {
          title,
          content,
          category,
          language,
          image: articleImage,
          id: articleId,
          date: new Date().toLocaleDateString(),
          createdAt: new Date().toISOString(),
          authorId: currentUser.uid,
          keywords: finalKeywords,
        });

        // If student, append to their list
        if (isStudent) {
          const studentDocRef = doc(getFirestore(), "students", currentUser.uid);
          const studentSnap = await getDoc(studentDocRef);
          if (studentSnap.exists()) {
            const sData = studentSnap.data();
            const articles = sData.articles || [];
            await updateDoc(studentDocRef, { articles: [...articles, newRef.id] });
          }
        }

        setSuccess(true);
        navigate(isStudent ? "/student-profile" : "/my-articles");
      }
    } catch (err) {
      console.error("Error saving article:", err);
      setError(t("articleEditPage.error.updateError", "Error saving the article."));
    }
  };

  const handleDelete = async () => {
    if (!currentUser) {
      setError(t("articleEditPage.error.notLoggedIn", "You must be logged in."));
      return;
    }
    try {
      const ref = doc(getFirestore(), "news", id);
      const snap = await getDoc(ref);
      if (isStudent && snap.exists() && snap.data().authorId !== currentUser.uid) {
        setError(t("articleEditPage.error.notAuthorized", "You are not authorized to delete this article."));
        return;
      }

      await deleteDoc(ref);

      if (isStudent) {
        const studentDocRef = doc(getFirestore(), "students", currentUser.uid);
        const studentSnap = await getDoc(studentDocRef);
        if (studentSnap.exists()) {
          const sData = studentSnap.data();
          const articles = sData.articles || [];
          await updateDoc(studentDocRef, {
            articles: articles.filter((aid) => aid !== id),
          });
        }
      }

      setSuccess(true);
      navigate(isStudent ? "/student-profile" : "/my-articles");
    } catch (err) {
      console.error("Error deleting article:", err);
      setError(t("articleEditPage.error.deleteError", "Error deleting the article."));
    }
  };

  const confirmDelete = () => {
    const confirmed = window.confirm(
      t("articleEditPage.deleteConfirmation", "Are you sure you want to delete this article?")
    );
    if (confirmed) handleDelete();
  };

  // ---------- Render ----------
  if (loading) return <div>{t("articleEditPage.loading", "Loading...")}</div>;
  if (error) return <div className="error-message">{error}</div>;

  return (
    <div className="article-edit-container">
      <h2>{id ? t("articleEditPage.titleEdit", "Edit Article") : t("articleEditPage.titleCreate", "Create Article")}</h2>
      {success && <p className="success-message">{t("articleEditPage.successMessage", "Saved successfully!")}</p>}

      <form onSubmit={handleSubmit} className="article-edit-form">
        {/* Title */}
        <div>
          <label htmlFor="title">{t("articleEditPage.form.titleLabel", "Title")}</label>
          <input
            type="text"
            id="title"
            value={title}
            onChange={(e) => setTitle(e.target.value)}
            required
          />
        </div>

        {/* Content */}
        <div>
          <div className="content-header">
            <label htmlFor="content">{t("articleEditPage.form.contentLabel", "Content")}</label>
            {inlineErrors.length > 0 && (
              <div className="markup-controls">
                <label className="markup-toggle">
                  <input
                    type="checkbox"
                    checked={showMarkups}
                    onChange={(e) => setShowMarkups(e.target.checked)}
                  />
                  {t("articleEditPage.form.showInlineMarkups", "Show inline markups")} ({inlineErrors.length})
                </label>
              </div>
            )}
          </div>
          <div className="editor-container">
            <InlineMarkupEditor
              content={content}
              onChange={handleContentChange}
              inlineErrors={inlineErrors}
              showMarkups={showMarkups}
              modules={quillModules}
              placeholder={t("articleEditPage.form.contentPlaceholder", "Start writing your article...")}
            />
          </div>
        </div>

        {/* Category (select + free entry) */}
        <div>
          <label htmlFor="categorySelect">{t("articleEditPage.form.categoryLabel", "Category")}</label>
          <div className="category-row">
            <select
              id="categorySelect"
              value={category}
              onChange={(e) => setCategory(e.target.value)}
            >
              {categories.map((c) => (
                <option key={c} value={c}>{c}</option>
              ))}
            </select>
            <input
              type="text"
              className="category-input"
              placeholder={t("articleEditPage.form.newCategoryPlaceholder", "New category...")}
              value={newCategory}
              onChange={(e) => setNewCategory(e.target.value)}
              onKeyDown={(e) => {
                if (e.key === "Enter") {
                  e.preventDefault();
                  if (newCategory) {
                    addCategory(newCategory);
                    setNewCategory("");
                  }
                }
              }}
            />
            <button
              type="button"
              className="add-category-button"
              onClick={() => {
                if (newCategory) {
                  addCategory(newCategory);
                  setNewCategory("");
                }
              }}
            >
              {t("articleEditPage.form.addCategory", "Add")}
            </button>
          </div>
          <small className="hint">
            {t("articleEditPage.form.categoryHint", "Pick a category or create a new one.")}
          </small>
        </div>

        {/* Language */}
        <div>
          <label htmlFor="language">{t("articleEditPage.form.languageLabel", "Language")}</label>
          <select id="language" value={language} onChange={(e) => setLanguage(e.target.value)}>
            <option value={t("articleEditPage.languages.english", "English")}>
              {t("articleEditPage.languages.english", "English")}
            </option>
            <option value={t("articleEditPage.languages.portuguese", "Portuguese")}>
              {t("articleEditPage.languages.portuguese", "Portuguese")}
            </option>
            <option value={t("articleEditPage.languages.spanish", "Spanish")}>
              {t("articleEditPage.languages.spanish", "Spanish")}
            </option>
          </select>
        </div>

        {/* Image Upload */}
        <div>
          <label htmlFor="imageUpload">{t("articleEditPage.form.imageLabel", "Cover image")}</label>
          <input type="file" id="imageUpload" accept="image/*" onChange={handleImageUpload} />
        </div>

        {/* Keywords */}
        <div className="keywords-field">
          <label htmlFor="keywordsInput">{t("articleEditPage.form.keywordsLabel", "Keywords")}</label>
          <div className="keywords-input-wrapper">
            <input
              id="keywordsInput"
              type="text"
              value={keywordInput}
              onChange={(e) => setKeywordInput(e.target.value)}
              onKeyDown={handleKeywordKeyDown}
              onPaste={handleKeywordPaste}
              placeholder={t("articleEditPage.form.keywordsPlaceholder", "Type a keyword and press Enter")}
            />
            <button
              type="button"
              className="add-keyword-button"
              onClick={() => {
                if (keywordInput) {
                  addKeyword(keywordInput);
                  setKeywordInput("");
                }
              }}
            >
              {t("articleEditPage.form.addKeyword", "Add")}
            </button>
          </div>

          {keywords.length > 0 && (
            <ul className="keywords-chips">
              {keywords.map((k) => (
                <li key={k} className="chip">
                  <span className="chip-text">{k}</span>
                  <button
                    type="button"
                    className="chip-remove"
                    aria-label={t("articleEditPage.form.removeKeyword", "Remove keyword")}
                    onClick={() => removeKeyword(k)}
                  >
                    ×
                  </button>
                </li>
              ))}
            </ul>
          )}
          <small className="keywords-hint">
            {t("articleEditPage.form.keywordsHint", "Tip: paste a comma- or line-separated list to add many at once.")}
          </small>
        </div>

        {/* Article ID */}
        {articleId && (
          <div className="article-id-display">
            <p>
              {t("articleEditPage.form.id", "ID")}: {articleId}
            </p>
          </div>
        )}

        <button type="submit" className="submit-button">
          {t("articleEditPage.form.submitButton", "Save")}
        </button>
      </form>

      {/* Delete (only when editing) */}
      {id && (
        <div className="delete-article-container">
          <button className="delete-button" onClick={confirmDelete}>
            {t("articleEditPage.form.deleteButton", "Delete")}
          </button>
        </div>
      )}

      {/* AI Analysis Panel */}
      {currentUser && content && content.trim().length > 0 && (
        <AIAnalysisPanel
          content={content}
          onAnalysisComplete={(analysis) => {
            console.log("Analysis completed:", analysis);
          }}
          onInlineErrorsUpdate={handleInlineErrorsUpdate}
        />
      )}
    </div>
  );
};

export default EditArticlePage;
